import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AppState } from '../../interfaces/app-state.interface';
import { BackendService } from '../new-http/backend.service';
import { Observable, BehaviorSubject } from 'rxjs';
import { filter } from 'rxjs/operators';
import { SuitabilityProfileFittingAcceptRequest, SuitabilityProfileFittingAcceptResponse, SuitabilityProfileFittingRequest, SuitabilityProfileFittingResponse, SuitabilityProfileResponse } from '../../models/santander-suitability/santander-suitability.model';
import { SettingsService } from '../settings/settings.service';
import { StorageService } from '../storage.service';

@Injectable({
  providedIn: 'root',
})
export class SantanderSuitabilityService {

  constructor(
    private backendService: BackendService,
    private store: Store<AppState>,
    private settingsService: SettingsService,
  ) {
  }

  public sessionId: string;

  /**
   * Obtém o perfil de suitability do investidor.
   *@returns Um `Observable<ProfileResponse>` com a resposta da API contendo o perfil do cliente.
   */
  public getProfile(): Observable<SuitabilityProfileResponse> {
    try {
      this.fillSessionId();
      return this.backendService.api.get(`/oq/v1/profile`);
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Verifica o enquadramento do investimento proposto com o perfil do investidor.
   * Envia um payload com detalhes do investimento para validação.
   *@param {SuitabilityProfileFittingRequest} payload - Os dados do investimento a serem verificados.
   *@returns Um `Observable<FittingResponse>` com a resposta da API sobre o enquadramento.
   */
  public checkFitting(payload: SuitabilityProfileFittingRequest): Observable<SuitabilityProfileFittingResponse> {
    this.fillSessionId();
    return this.backendService.api.post(
      `/oq/v1/profile/fittings`,
      payload
    );
  }

  /**
   * Envia a aceitação dos termos de enquadramento do investimento.
   *@param {AcceptTermPayload} payload - Os dados da aceitação do termo.
   *@returns Um `Observable<FittingAcceptResponse>` com a resposta da API após o aceite do termo.
   */
  public acceptTerm(payload: SuitabilityProfileFittingAcceptRequest): Observable<SuitabilityProfileFittingAcceptResponse> {
    this.fillSessionId();
    return this.backendService.api.post(
      `/oq/v1/profile/fittingAccept`,
      payload
    );
  }

  private fillSessionId = () => {
    this.store
      .select(({ authentication }) => authentication.currentUser)
      .pipe(
        filter((authentication) => !!authentication)
      )
      .subscribe((user) => {
        this.sessionId = user.sessionId
      })
  }

  public mustShowPostLoginCommunication() {
    const { showPostLoginCommunication } = this.settingsService.getConfigurationKeyValue('suitabilitySantander');

    return showPostLoginCommunication;
  }

  public mustShowCommunication() {
    const config = this.settingsService.getConfigurationKeyValue('suitabilitySantander');

    if (!config) {
      return false;
    }

    const { showAnticipateDialogs } = config;

    return !!showAnticipateDialogs;
  }

  public isInPreAnticipation() {
    const targetDate = this.getReleaseDate();

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    const isPreAnticipation = today < targetDate;

    return isPreAnticipation;
  }

  public getReleaseDate(): Date {
    const { releaseDate } = this.settingsService.getConfigurationKeyValue('suitabilitySantander') ?? {};
    if (!releaseDate || typeof releaseDate !== 'string') {
        return new Date(0);
    }

    const [year, month, day] = releaseDate.split('-').map(Number);

    return new Date(year, month - 1, day);
  }

  public getClientSegment() {

    const cookieTrader = StorageService.getCookie("hubUserTraderStatus") === "true";
    const cookieExclusive = StorageService.getCookie("hubUserExclusiveStatus") === "true";

    const clientSegment = cookieExclusive ? 'Exclusivo' : cookieTrader ? 'Trader' : 'Varejo';

    return clientSegment;
  }

}
